import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, Crosshair } from "lucide-react";
import { useCertero } from "@/context/CerteroContext";
import { IMG, track } from "@/data/content";import { EASE } from "@/components/Reveal";

const LINES = [
  { t: "Tecnología", c: "text-mist" },
  { t: "que transforma", c: "text-mist" },
  { t: "el campo.", c: "text-certero" },
];

export default function Hero() {
  const { openLead, scrollToId } = useCertero();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section id="inicio" ref={ref} className="relative flex min-h-[100svh] items-end overflow-hidden bg-ink">
      {/* Fondo: video cinematográfico con zoom + parallax sutil */}
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.div
          className="h-full w-full"
          initial={{ scale: 1.18 }}
          animate={{ scale: 1.02 }}
          transition={{ duration: 9, ease: EASE }}
        >
          <video
            data-testid="hero-video"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={IMG.hero}
            className="h-full w-full object-cover"
          >
            <source src={IMG.videoHero} type="video/mp4" />
          </video>
        </motion.div>
      </motion.div>
      <div className="absolute inset-0 bg-abyss/55" aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-abyss via-abyss/40 to-transparent" aria-hidden="true" />

      {/* HUD: marco y retícula */}
      <div className="pointer-events-none absolute inset-6 sm:inset-10 z-10" aria-hidden="true">
        <span className="absolute left-0 top-0 h-5 w-5 border-l border-t border-certero/50" />
        <span className="absolute right-0 top-0 h-5 w-5 border-r border-t border-certero/50" />
        <span className="absolute bottom-0 left-0 h-5 w-5 border-b border-l border-certero/50" />
        <span className="absolute bottom-0 right-0 h-5 w-5 border-b border-r border-certero/50" />
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="pointer-events-none absolute right-10 top-1/2 hidden -translate-y-1/2 flex-col items-center gap-4 lg:flex z-10"
        aria-hidden="true"
      >
        <motion.span animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2.4, repeat: Infinity }}>
          <Crosshair className="h-8 w-8 text-certero/70" strokeWidth={1} />
        </motion.span>
        <span className="h-24 w-px bg-gradient-to-b from-certero/60 to-transparent" />
        <span className="font-mono text-[10px] tracking-[0.2em] text-mist/60 [writing-mode:vertical-rl]">
          RTK LISTO · SANTA CRUZ, BOLIVIA
        </span>
      </motion.div>

      {/* Contenido */}
      <div className="relative z-20 mx-auto w-full max-w-[1400px] px-4 pb-24 pt-32 sm:px-8 lg:px-12 lg:pb-28">
        <motion.p
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.8, ease: EASE }}
          className="mb-6 flex items-center gap-3 font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-certero"
        >
          <span className="h-2 w-2 rounded-full bg-certero motion-safe:animate-pulse" />
          CERTERO SRL — DJI Agriculture en Bolivia
        </motion.p>

        <h1 data-testid="hero-title" className="font-display font-800 uppercase leading-[0.95] tracking-tight">
          {LINES.map((l, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block text-[clamp(2.6rem,8.5vw,6.5rem)] ${l.c}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.45 + i * 0.16, duration: 1, ease: EASE }}
              >
                {l.t}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8, ease: EASE }}
          className="mt-7 max-w-xl text-base leading-relaxed text-mist/85 sm:text-lg"
        >
          Soluciones DJI Agriculture, servicio técnico y tecnología de precisión para una nueva
          generación de agricultura.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.8, ease: EASE }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <button
            data-testid="hero-cta-cotizar"
            onClick={() => { track("cotizar_click", { origen: "hero" }); openLead("cotizacion"); }}
            className="group relative h-14 overflow-hidden rounded-full bg-certero px-9 font-display text-sm font-bold uppercase tracking-[0.15em] text-ink transition-[transform,background-color] duration-300 hover:bg-[#4bc443] active:scale-95"
          >
            <span className="relative z-10">Cotizar ahora</span>
          </button>
          <button
            data-testid="hero-cta-explorar"
            onClick={() => { track("explorar_click", { origen: "hero" }); scrollToId("drones"); }}
            className="group flex h-14 items-center justify-center gap-3 rounded-full border border-mist/30 px-9 font-display text-sm font-bold uppercase tracking-[0.15em] text-mist transition-[border-color,background-color] duration-300 hover:border-certero hover:bg-certero/10"
          >
            Ver drones
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
          </button>
        </motion.div>
      </div>

      {/* Indicador scroll */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-20 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex"
        aria-hidden="true"
      >
        <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-mist/50">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="h-8 w-px bg-gradient-to-b from-certero to-transparent"
        />
      </motion.div>
    </section>
  );
}
